import { useEffect, useMemo, useRef, useState } from "react";
import {
  type AssetCategory,
  type AssetRecord,
  defaultPathForFile,
  deleteAsset,
  inferCategoryFromMime,
  inferCategoryFromPath,
  listAssets,
  syncAssets,
  uploadAsset,
} from "../storage/assetStore";

interface Props {
  projectId: string;
  onChange?: () => void;
}

const CATEGORIES: AssetCategory[] = ["bg", "chara", "face", "bgm", "se", "voice", "other"];

export function AssetPanel({ projectId, onChange }: Props) {
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrls = useRef<Map<string, string>>(new Map());

  const refresh = async (sync: boolean) => {
    if (sync) {
      const all = await syncAssets(projectId);
      setAssets(all);
    } else {
      const all = await listAssets(projectId);
      setAssets(all);
    }
  };

  useEffect(() => {
    setAssets([]);
    void refresh(true);
    return () => {
      for (const url of objectUrls.current.values()) URL.revokeObjectURL(url);
      objectUrls.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const newMap = new Map<string, string>();
    for (const a of assets) {
      const existing = objectUrls.current.get(a.path);
      if (existing) {
        newMap.set(a.path, existing);
      } else if (a.mime.startsWith("image/")) {
        newMap.set(a.path, URL.createObjectURL(a.blob));
      }
    }
    for (const [path, url] of objectUrls.current) {
      if (!newMap.has(path)) URL.revokeObjectURL(url);
    }
    objectUrls.current = newMap;
  }, [assets]);

  const onFilesPicked = async (files: FileList | File[]) => {
    setBusy(true);
    try {
      const arr = Array.from(files);
      for (const file of arr) {
        const mimeCat = inferCategoryFromMime(file.type);
        const path = defaultPathForFile(file, mimeCat);
        await uploadAsset(projectId, path, file, file.type);
      }
      await refresh(false);
      onChange?.();
    } catch (e) {
      alert(`アップロード失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (path: string) => {
    if (!confirm(`${path} を削除しますか?`)) return;
    const url = objectUrls.current.get(path);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrls.current.delete(path);
    }
    await deleteAsset(projectId, path);
    await refresh(false);
    onChange?.();
  };

  const onRename = async (rec: AssetRecord) => {
    const next = prompt("新しいパス", rec.path);
    if (!next || next === rec.path) return;
    await uploadAsset(projectId, next, rec.blob, rec.mime);
    await deleteAsset(projectId, rec.path);
    await refresh(false);
    onChange?.();
  };

  const onResync = async () => {
    setBusy(true);
    try {
      await refresh(true);
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<AssetCategory, AssetRecord[]>();
    for (const cat of CATEGORIES) map.set(cat, []);
    for (const a of assets) {
      const list = map.get(a.category) ?? map.get("other")!;
      list.push(a);
    }
    return map;
  }, [assets]);

  return (
    <div className="asset-panel">
      <div
        className={"asset-drop" + (dragOver ? " drag-over" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length > 0) {
            void onFilesPicked(e.dataTransfer.files);
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="asset-drop-icon">📁</div>
        <div>{busy ? "アップロード中..." : "ファイルをドロップ or クリックして選択"}</div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,audio/*"
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files) {
              void onFilesPicked(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      <div className="asset-toolbar">
        <button className="btn btn-tiny btn-secondary" onClick={() => void onResync()} disabled={busy}>
          🔄 sync
        </button>
        <span className="asset-count">{assets.length} 個</span>
      </div>

      <div className="asset-list">
        {CATEGORIES.map((cat) => {
          const list = grouped.get(cat) ?? [];
          if (list.length === 0) return null;
          return (
            <div key={cat} className="asset-group">
              <div className="asset-group-title">{cat.toUpperCase()}</div>
              <div className="asset-grid">
                {list.map((a) => (
                  <AssetCard
                    key={a.path}
                    rec={a}
                    previewUrl={objectUrls.current.get(a.path)}
                    onDelete={() => void onDelete(a.path)}
                    onRename={() => void onRename(a)}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {assets.length === 0 && (
          <div className="asset-empty">
            アップロードされたアセットはまだありません。
            <br />
            画像 / 音声ファイルをドロップしてください。
          </div>
        )}
      </div>
    </div>
  );
}

function AssetCard({
  rec,
  previewUrl,
  onDelete,
  onRename,
}: {
  rec: AssetRecord;
  previewUrl: string | undefined;
  onDelete: () => void;
  onRename: () => void;
}) {
  const isImage = rec.mime.startsWith("image/");
  const isAudio = rec.mime.startsWith("audio/");
  return (
    <div
      className="asset-card"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "copy";
        e.dataTransfer.setData(
          "application/x-pix-asset",
          JSON.stringify({ path: rec.path, mime: rec.mime, category: rec.category }),
        );
        e.dataTransfer.setData("text/plain", rec.path);
      }}
      title="グリッドのセルへドラッグして挿入できます"
    >
      <div className="asset-thumb">
        {isImage && previewUrl ? (
          <img src={previewUrl} alt={rec.path} />
        ) : isAudio ? (
          <audio controls preload="none" style={{ width: "100%" }}>
            <source src={URL.createObjectURL(rec.blob)} type={rec.mime} />
          </audio>
        ) : (
          <div className="asset-thumb-fallback">📄</div>
        )}
      </div>
      <div className="asset-meta">
        <div className="asset-path" title={rec.path}>
          {rec.path}
        </div>
        <div className="asset-info">
          {formatBytes(rec.size)} · {rec.mime || "?"}
        </div>
        <div className="asset-actions">
          <button className="btn btn-tiny btn-secondary" onClick={onRename}>
            rename
          </button>
          <button className="btn btn-tiny btn-warn" onClick={onDelete}>
            delete
          </button>
        </div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}
